import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { get } from 'lodash-es';
import classnames from 'classnames';

import { getPlural } from 'client/utils/plural';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { getDisplayHeadline } from 'site-modules/shared/components/native-ad/utils/offer-incentives-ad-utils';

import { usurpSsaStl } from 'site-modules/shared/components/native-ad/usurp-site-served-ad/usurp-site-served-ad-styles';
import { svgCarTrims, svgColorPalette, svgStreeringWheel } from 'client/images/inline-svgs/ads/buildprice-ad-icons';

import { VehicleEntities } from 'client/data/models/vehicle';
import { isUsedState } from 'site-modules/shared/utils/publication-states';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ConquestRibbon } from 'site-modules/shared/components/native-ad/native-ad-components/conquest-ribbon/conquest-ribbon';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';
import { CpoFeaturesList } from 'site-modules/shared/components/native-ad/native-ad-components/cpo-features/cpo-features-list';
import { SRPCreativeConfigPropTypes } from 'client/data/models/native-ads-creative-config';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

const CTA = 'VIEW ALL OFFERS';
const HEADLINE_DEFAULT = 'Special offers available';

function getMsrp(msrp, vehicleInfo) {
  const msrpPrice = typeof msrp === 'string' ? msrp.trim() : get(vehicleInfo, 'submodels[0].MSRP');
  return getPriceString(msrpPrice, '');
}

function AtGlanceItem({ icon, iconClassName, text }) {
  return (
    <li className="pe-0_5 text-nowrap mb-1 d-flex align-items-center">
      {iconClassName ? <i className={iconClassName} /> : icon}
      <ContentFragment componentToUse="span">{text}</ContentFragment>
    </li>
  );
}

AtGlanceItem.propTypes = {
  icon: PropTypes.node,
  iconClassName: PropTypes.string,
  text: PropTypes.string.isRequired,
};

export function UsurpSiteServedContent({
  ctaURL,
  ctaTarget,
  adClickTrackingListener,
  creativeConfig,
  isConquest = false,
  targetVehicle,
  isCpoFeaturesEnabled = false,
}) {
  const {
    zipCode,
    ipZipCode,
    offersNumber,
    creativeConfigData: {
      headline,
      logo,
      photo,
      photoDisclaimer,
      linkDisplayUrl,
      cta,
      msrp,
      vehicleInfo,
      trimNames,
      cross_shop_features: crossShopFeatures,
    },
  } = creativeConfig;

  const headlineText = getDisplayHeadline(headline, offersNumber, zipCode, ipZipCode, HEADLINE_DEFAULT, 14, 10);
  const subHeadLineText = get(creativeConfig, 'creativeConfigData.subheadline', '');
  const msrpPrice = getMsrp(msrp, vehicleInfo);
  const colorCnt = get(vehicleInfo, 'submodels[0].extColorCnt');
  const trimCnt = get(trimNames, 'length');
  const packageCnt = get(vehicleInfo, 'submodels[0].pkgCnt');
  const ctaDisplay = cta || CTA;

  const isUsed = isUsedState(targetVehicle?.publicationState);

  const renderFeatures = isCpoFeaturesEnabled ? (
    <CpoFeaturesList isUsurpAd make={vehicleInfo?.makeName} />
  ) : (
    <ul style={usurpSsaStl.ul} className="at-a-glance-container size-14 fw-normal mt-0_5" aria-label="At a glance">
      {!!colorCnt && <AtGlanceItem icon={svgColorPalette} text={`${colorCnt} ${getPlural('Color', colorCnt)}`} />}

      {!!trimCnt && <AtGlanceItem icon={svgCarTrims} text={`${trimCnt} ${getPlural('Trim', trimCnt)}`} />}

      {!!packageCnt && (
        <AtGlanceItem icon={svgStreeringWheel} text={`${packageCnt} ${getPlural('Package', packageCnt)}`} />
      )}
    </ul>
  );

  return (
    <Fragment>
      <div className="pos-r jelly-bean" style={{ height: '200px' }}>
        <figure className="pos-r m-0 mx-auto" style={usurpSsaStl.jellyBean}>
          <img src={photo} alt="" loading="lazy" style={usurpSsaStl.img} />
        </figure>
        <div
          className="disclaimer persistent-disclaimer text-gray-dark text-end pe-1"
          style={usurpSsaStl.persistentDisclaimer}
        >
          {photoDisclaimer}
        </div>
      </div>
      {isConquest ? (
        <ConquestRibbon style={usurpSsaStl.conquestTopLeft} />
      ) : (
        <div className="oem-logo-top-left" style={usurpSsaStl.oemLogoTopLeft}>
          <img className="oem-logo" src={logo} alt="" loading="lazy" style={usurpSsaStl.oemLogo} />
        </div>
      )}
      <div className="p-1 d-flex flex-column" style={{ flex: 'auto' }}>
        <Row className="mx-0 mb-1 pb-0_75 flex-nowrap justify-content-between" style={usurpSsaStl.borderBottom}>
          <Col className="ps-0 pe-0_5">
            <ContentFragment componentToUse="h2" classes="mb-0_5 size-16 fw-bold text-blue-50">
              {headlineText}
            </ContentFragment>
            <ContentFragment classes="medium" inlineStyle={usurpSsaStl.headline}>
              {subHeadLineText}
            </ContentFragment>
          </Col>
          {!isUsed && !isCpoFeaturesEnabled && !!msrpPrice && (
            <Col xs={5} style={usurpSsaStl.borderLeft} className="pe-0 text-end">
              <ContentFragment classes="heading-3">{msrpPrice}</ContentFragment>
              <div className="small">starting MSRP</div>
            </Col>
          )}
        </Row>

        <Experiment name="ads-12584" showDefault>
          <Recipe name="ctrl" isDefault>
            {renderFeatures}
          </Recipe>
          <Recipe name="chal">
            {crossShopFeatures?.length ? (
              <ul
                style={usurpSsaStl.ul}
                className="at-a-glance-container size-14 fw-normal mt-0_5"
                aria-label="At a glance"
              >
                {crossShopFeatures.map(feature => (
                  <AtGlanceItem text={feature} iconClassName="icon-checkmark text-blue-50 me-0_5" key={feature} />
                ))}
              </ul>
            ) : (
              renderFeatures
            )}
          </Recipe>
        </Experiment>

        <div style={{ marginTop: 'auto' }}>
          <a
            className="site-served-ad-link no-focus text-decoration-none"
            href={ctaURL}
            rel="noopener noreferrer"
            target={ctaTarget}
            onClick={adClickTrackingListener}
          >
            <span
              className={classnames('cta btn rounded-8 w-100 btn-sm text-capitalize size-16 fw-medium', {
                'btn-blue-50': !isConquest,
                'btn-outline-blue-50': isConquest,
              })}
            >
              <CtaButtonText text={ctaDisplay} />
            </span>
          </a>
          <div
            className="small text-blue-50 text-center mt-0_25 mb-0_75"
            style={{ lineHeight: '.875rem' }}
            role="presentation"
          >
            {linkDisplayUrl}
          </div>
        </div>
      </div>
    </Fragment>
  );
}

UsurpSiteServedContent.propTypes = {
  ctaURL: PropTypes.string.isRequired,
  ctaTarget: PropTypes.string.isRequired,
  adClickTrackingListener: PropTypes.func.isRequired,
  creativeConfig: SRPCreativeConfigPropTypes.isRequired,
  targetVehicle: VehicleEntities.Vehicle.isRequired,
  isConquest: PropTypes.bool,
  isCpoFeaturesEnabled: PropTypes.bool,
};
